import React from 'react';
import { Button as DlsButton } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { parseHtml } from '../../utils/util';
import useButtonDisable from '../../CustomHooks/useButtonDisable';
import { getIcon } from './Icons';

/**
 * button component return DLS button component
 * @param {*} param0
 */
/* eslint complexity: ["error", 36] -- need to increase complexity */
const Button = ({
  fieldId,
  fieldLabel,
  fieldName,
  cssProperties,
  fieldValidation,
  handleBack,
  entityToAdd,
  isSubmitButtonDisable,
  isFormSubmission,
  setEntityId,
  handleContinue,
  disabledButtons,
  isStateControlled = false,
  isOverflowAllowed = false,
  buttonType,
  popUpToClose,
  setShowPopUp,
  triggerPopUpId,
  setEvaluatePopUpId,
  curPageValues,
  filledFormData,
  saveDataIds = [],
  setFormSubmissionPayload,
  keysToUpdate = {},
  dispatchIdsState,
  idsToClear,
  fieldIdsToClear,
  iconName,
  saveItrackerStatusApi,
  pageStatus,
}) => {
  const scopedValidationKey = fieldId === 'addEntity' && typeof fieldValidation?.scopedValidationKey === 'string'
    ? fieldValidation.scopedValidationKey
    : '';
  const scopedValidationFieldIds = fieldId === 'addEntity' && Array.isArray(fieldValidation?.validateFieldIds)
    ? fieldValidation.validateFieldIds
    : [];
  const shouldUseScopedValidation = scopedValidationKey !== '' && scopedValidationFieldIds.length > 0;
  const btnType = buttonType || (
    ['back', 'saveAndContinue', 'saveDataAndContinue', 'updateKeysToFormSubmission', 'cancelEntityEdit'].includes(fieldId)
    || fieldValidation?.redirectionURL ? 'button' : 'submit'
  );
  const isDisabled = useButtonDisable({
    fieldValidation,
    isStateControlled,
    isSubmitButtonDisable,
    fieldName,
    disabledButtons,
    fieldId,
  });

  /* eslint complexity: ["error", 36] -- need to increase complexity */
  const handleOnClick = () => {
    if (entityToAdd) {
      setEntityId(entityToAdd);
    }
    if (fieldId === 'back') {
      if (fieldValidation && fieldValidation.redirectionURL) {
        window.location.href = fieldValidation.redirectionURL;
      } else {
        handleBack();
      }
    } else if (
      fieldId === 'saveDataAndContinue'
      || fieldId === 'saveDataToS3WithoutPW'
    ) {
      /* eslint-disable no-restricted-syntax -- need */
      const keys = saveDataIds && saveDataIds.length > 0
        ? saveDataIds
        : Object.keys(curPageValues);
      if (keys.length > 0) {
        for (const key of keys) {
          filledFormData[key] = curPageValues[key];
        }
      }
      /* eslint-enable no-restricted-syntax -- need */
      if (fieldId === 'saveDataToS3WithoutPW') {
        setFormSubmissionPayload({
          ...filledFormData,
          elemId: 'saveDataToS3',
        });
      } else {
        handleContinue();
      }
    } else if (fieldId === 'saveAndContinue') {
      if (Object.keys(keysToUpdate).length > 0) {
        setFormSubmissionPayload({ ...filledFormData, ...keysToUpdate, elemId: null });
      }
      handleContinue();
    } else if (fieldId === 'reset') {
      dispatchIdsState({
        toggleState: !idsToClear.toggleState,
        ids: { ...fieldIdsToClear },
      });
    } else if (fieldId === 'downloadEsignPdf') {
      document.querySelector('#esign-downloadpdf')?.click();
    } else if (fieldId === 'submitEsignPdf') {
      document.querySelector('#esign-submit')?.click();
    } else if (
      fieldId === 'updateKeysToFormSubmission'
      && Object.keys(keysToUpdate).length > 0
    ) {
      setFormSubmissionPayload({
        ...filledFormData,
        ...keysToUpdate,
        elemId: null,
      });
    } else if (fieldValidation && fieldValidation.redirectionURL) {
      if (fieldValidation.isRedirectionInSameTab) {
        window.open(fieldValidation.redirectionURL, '_self');
      } else {
        window.open(fieldValidation.redirectionURL, '_blank');
      }
    } else if (triggerPopUpId && triggerPopUpId !== '') {
      setEvaluatePopUpId(triggerPopUpId);
    } else if (fieldId === 'cancelEntityEdit') {
      setFormSubmissionPayload({ ...filledFormData, ...keysToUpdate, elemId: 'cancelEntityEdit' });
    }

    if (popUpToClose && popUpToClose !== '') {
      setShowPopUp('');
    }
    if (pageStatus) {
      saveItrackerStatusApi?.(pageStatus);
    }
  };

  const getButtonVariant = () => {
    if (cssProperties.type === 'ButtonSecondary' || fieldLabel?.cssProperties?.style?.includes('btn-secondary')) {
      return 'secondary';
    }
    if (cssProperties.type === 'ButtonTertiary' || fieldLabel?.cssProperties?.style?.includes('btn-tertiary')) {
      return 'tertiary';
    }
    return 'primary';
  };

  const normalizedButtonClass = fieldLabel?.cssProperties?.style.includes('btn-block')
    && fieldLabel?.cssProperties?.style.includes('margin-auto-lr')

    ? fieldLabel?.cssProperties?.style
      .replace(/\bbtn-block\b/g, 'display-block')
      .replace(/\s+/g, ' ')
      .trim()
    : fieldLabel?.cssProperties?.style;

  return (
    <div className={cssProperties.style}>
      <DlsButton
        variant={getButtonVariant()}
        id={fieldId}
        name={fieldName || fieldId}
        type={btnType}
        className={`${normalizedButtonClass} width-full`}
        aria-disabled={!fieldValidation?.loading && (isDisabled || isFormSubmission)}
        isLoading={fieldValidation?.loading && isFormSubmission}
        onClick={handleOnClick}
        allowOverflow={isOverflowAllowed}
        icon={getIcon(iconName)}
        formNoValidate={shouldUseScopedValidation}
        data-keys-to-update={JSON.stringify(keysToUpdate)}
        data-validate-field-ids={JSON.stringify(scopedValidationFieldIds)}
        data-scoped-validation-key={scopedValidationKey}
      >
        {parseHtml(fieldLabel.label)}
      </DlsButton>
    </div>
  );
};

Button.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldId: PropTypes.string,
  fieldName: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  handleBack: PropTypes.func,
  isSubmitButtonDisable: PropTypes.bool,
  isFormSubmission: PropTypes.bool,
  setEntityId: PropTypes.func,
  handleContinue: PropTypes.func,
  entityToAdd: PropTypes.string,
  disabledButtons: PropTypes.objectOf(PropTypes.shape),
  isStateControlled: PropTypes.bool,
  isOverflowAllowed: PropTypes.bool,
  buttonType: PropTypes.string,
  popUpToClose: PropTypes.string,
  setShowPopUp: PropTypes.func,
  triggerPopUpId: PropTypes.string,
  setEvaluatePopUpId: PropTypes.func,
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  saveDataIds: PropTypes.arrayOf(PropTypes.string),
  setFormSubmissionPayload: PropTypes.func,
  keysToUpdate: PropTypes.objectOf(PropTypes.shape),
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  dispatchIdsState: PropTypes.func,
  fieldIdsToClear: PropTypes.objectOf(PropTypes.shape),
  iconName: PropTypes.string,
  saveItrackerStatusApi: PropTypes.func,
  pageStatus: PropTypes.string,
};

export default Button;
